// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext, useRef  } from 'react';
import { useNavigate } from 'react-router-dom';

// Contexte d'authentification
const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

// Hook personnalisé useAuth
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
    }
    return context;
};

// Provider d'authentification
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const inactivityTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Vérifier l'authentification au chargement
    useEffect(() => {
        const checkAuth = () => {
            const storedUser = localStorage.getItem('user');
            const userId = localStorage.getItem('userId');
            
            if (storedUser && userId) {
                try {
                    const userData = JSON.parse(storedUser);
                    setUser(userData);
                    setIsAuthenticated(true);
                    // console.log('Utilisateur authentifié:', userData.email);
                } catch (error) {
                    console.error('Erreur lors du chargement des données utilisateur:', error);
                    localStorage.removeItem('user');
                    localStorage.removeItem('userId');
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    // Fonction de connexion
    const login = (userData) => {
        // Stocker les données utilisateur
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('userId', userData.id);
        localStorage.setItem('userEmail', userData.email);
        localStorage.setItem('lastActivity', Date.now().toString());
        
        setUser(userData);
        setIsAuthenticated(true);

        // Démarrer le timer
        setTimeout(() => resetInactivityTimer(), 1000);
        // console.log('Connexion réussie pour:', userData.email);
    };

    // Fonction de déconnexion
    const logout = () => {

        // Annuler le timer
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }

        // Supprimer toutes les données de session
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('groupe_id');
        localStorage.removeItem('accesGroupe');
        
        setUser(null);
        setIsAuthenticated(false);
        
        navigate('/');
        // console.log('Déconnexion réussie');
    };


    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }

        if (isAuthenticated) {
            // console.log('Réinitialisation du timer d\'inactivité');
            lastActivityRef.current = Date.now();
            localStorage.setItem('lastActivity', Date.now().toString());
            
            inactivityTimerRef.current = setTimeout(() => {
                // console.log('Inactivité détectée - Déconnexion automatique');
                logout('Déconnecté pour inactivité (1h)');
            }, INACTIVITY_TIMEOUT);
        }
    };

    // Vérifier l'authentification au chargement
    useEffect(() => {
        const checkAuth = () => {
            const storedUser = localStorage.getItem('user');
            const userId = localStorage.getItem('userId');
            
            if (storedUser && userId) {
                try {
                    const userData = JSON.parse(storedUser);
                    setUser(userData);
                    setIsAuthenticated(true);
                    
                    // Vérifier la dernière activité
                    const lastActivity = localStorage.getItem('lastActivity');
                    if (lastActivity) {
                        const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
                        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
                            // console.log('Session expirée');
                            // Supprimer les données sans rediriger (déjà sur la page)
                            localStorage.removeItem('user');
                            localStorage.removeItem('userId');
                            localStorage.removeItem('userEmail');
                            localStorage.removeItem('lastActivity');
                            setUser(null);
                            setIsAuthenticated(false);
                            setLoading(false);
                            return;
                        }
                    }
                    
                    // console.log('Utilisateur authentifié:', userData.email);
                    // Démarrer le timer après un petit délai
                    setTimeout(() => resetInactivityTimer(), 500);
                } catch (error) {
                    console.error('Erreur:', error);
                    localStorage.removeItem('user');
                    localStorage.removeItem('userId');
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    // Écouter les événements d'activité
    useEffect(() => {
        if (!isAuthenticated) return;

        const handleActivity = () => {
            localStorage.setItem('lastActivity', Date.now().toString());
            resetInactivityTimer();
        };

        // Écouter toutes les interactions utilisateur
        const events = [
            'mousedown', 'mousemove', 'keydown', 
            'touchstart', 'scroll', 'click', 
            'focus', 'wheel'
        ];
        
        events.forEach(event => {
            document.addEventListener(event, handleActivity);
        });

        // Nettoyage
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [isAuthenticated]);




    // Vérifier si l'utilisateur a un accès spécifique
    const hasAccess = (permission) => {
        if (!user) return false;
        
        // Admin a tous les accès
        if (user.role === 'administrateur') return true;
        
        // Vérifier les permissions stockées
        const storedAccess = localStorage.getItem('accesGroupe');
        if (storedAccess) {
            try {
                const accessList = JSON.parse(storedAccess);
                return accessList.includes(permission);
            } catch (e) {
                return false;
            }
        }
        
        return false;
    };

    // Valeurs exposées par le contexte
    const value = {
        user,
        isAuthenticated,
        loading,
        userId: user?.id || localStorage.getItem('userId'),
        login,
        logout,
        hasAccess,
        isAdmin: user?.role === 'administrateur',
        userName: user ? `${user.prenoms || ''} ${user.nom || ''}`.trim() : '',
        userEmail: user?.email || localStorage.getItem('userEmail'),
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default useAuth;